import { FC } from 'react';
import { Root, Trigger } from '@radix-ui/react-context-menu';
import {
  Chart as ChartJS,
  CategoryScale,
  ChartData,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import tw from 'twin.macro';
import styled from 'styled-components';

import defaultOptions from './defaultOption';
import strings from '../../language/Swedish';
import { IndexValueType } from '../../types/apiTypes';

const Container = styled.div`
  ${tw`w-full grid place-items-center min-h-96 max-w-[1920px] mx-auto`}
`;

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Legend,
  Title,
  Tooltip
);

const defaultFillOptions = {
  fill: true,
  fillColor: 'rgb(53 125 66 / 20%)',
  borderColor: 'rgb(53 125 66)',
  tension: 0,
  pointRadius: 0,
};

interface Props {
  data: IndexValueType[];
  chartLabel?: string;
  options?: ChartOptions<'line'>;
  className?: string;
}

const LineChart: FC<Props> = ({ data, chartLabel, options, className }) => {
  const labels = data.map((item) => {
    const date = new Date(item.average_date);

    return `${strings.months[date.getMonth()]} ${date.getFullYear()}`;
  });
  const dataPoints = data.map((item) => item.value);

  const getMaxValueYaxis = () => {
    const maxValue = dataPoints.reduce(
      (max, current) => (max && current && max > current ? max : current),
      dataPoints[0]
    );

    return maxValue + maxValue / 10;
  };

  const chartData: ChartData<'line', (number | null)[], string> = {
    labels: labels,
    datasets: [
      {
        label: chartLabel,
        data: dataPoints,
        ...defaultFillOptions,
      },
    ],
  };

  return (
    <Root>
      <Trigger>
        <Container className={className}>
          <Line
            data={chartData}
            options={{ ...defaultOptions(getMaxValueYaxis()), ...options }}
          />
        </Container>
      </Trigger>
    </Root>
  );
};

export default LineChart;
